import {
  Component,
  forwardRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  HostListener,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { SearchBoxComponent } from '../search-box/search-box.component';

const STRING_CONTROL_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SearchSelectComponent),
  multi: true,
};

@Component({
  selector: 'uikit-search-select',
  templateUrl: 'search-select.component.html',
  styleUrls: ['search-select.component.scss'],
  providers: [STRING_CONTROL_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchSelectComponent implements ControlValueAccessor, OnInit {
  @ViewChild('selectSearch', { static: true }) selectSearch: ElementRef;
  @ViewChild(SearchBoxComponent) SearchBox: SearchBoxComponent;
  @Output() selection = new EventEmitter();
  @Input() options: any[];
  @Input() alwaysFirstValue: string; // if this value in options, it will remain at the top (not considered in sorting)
  @Input() selected: any;
  @Input() showSearch = false;
  @Input() displayProperty: string;
  @Input() formControlProperty: string;
  @Input() sorted = false;
  @Input() placeholderText: string;
  @Input() width = 200;
  @Input() dropdownWidth: string;
  // Hack to handle reactive forms.
  @Input() setValues: boolean;
  @Input() closeOnClick: boolean;
  searchTerm: string;
  isSelecting: boolean;

  protected innerValue: string;
  private onTouch: any;
  private onModelChange: any;

  ngOnInit(): void {
    if (!this.dropdownWidth) {
      this.dropdownWidth = String(this.computedWidth) + 'px';
    }
  }

  optionClicked(option: any, event: any): void {
    if (this.setValues) {
      this.value = this.formControlProperty ? option[this.formControlProperty] : option;
      this.selected = option;
    }
    if (this.closeOnClick) {
      this.isSelecting = false;
    }
    this.selection.emit(option);
    event.stopPropagation();
  }

  optionText(option: any): string {
    if (!option) {
      return this.placeholderText;
    }

    if (this.displayProperty && option) {
      return option[this.displayProperty];
    }
    return option;
  }

  selectedText(selected: any): string {
    if (this.value) {
      if (this.displayProperty) {
        if (selected) {
          return selected[this.displayProperty];
        }
      }
      return this.value;
    }

    return this.optionText(selected);
  }

  @HostListener('document: click', ['$event'])
  toggleActionItems(event: any): void {
    const selectSearchClicked = this.selectSearch.nativeElement.contains(event.target);
    if (this.isSelecting && selectSearchClicked) {
      this.isSelecting = false;
    } else {
      this.isSelecting = selectSearchClicked;
      if (this.showSearch) {
        this.SearchBox.focusOnSearchBox();
      }
    }
  }

  filteredOptions(term: string): any[] {
    if (this.sorted) {
      this.options.sort((a, b) => {
        const val1 = a[this.displayProperty];
        const val2 = b[this.displayProperty];

        if (this.alwaysFirstValue && val1.toLowerCase() === this.alwaysFirstValue.toLowerCase()) {
          return -1;
        }

        return val1.localeCompare(val2);
      });
    }
    return term
      ? this.options.filter((option) => this.optionText(option).toLowerCase().indexOf(term.toLowerCase()) !== -1)
      : this.options;
  }

  dropdownIcon(): string {
    if (!this.isSelecting) {
      return 'arrow_drop_down';
    } else {
      return 'arrow_drop_up';
    }
  }

  get selectClass(): string {
    return this.isSelecting ? 'selected' : 'unselected';
  }

  get computedWidth(): number {
    return this.width - 10;
  }

  get value(): string {
    return this.innerValue;
  }

  set value(v: string) {
    if (this.innerValue !== v) {
      this.innerValue = v;
      if (this.onModelChange) {
        this.onModelChange(v);
      }
    }
  }

  writeValue(v: string): void {
    if (this.innerValue !== v) {
      this.innerValue = v;
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }
}
